import { useEffect } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, BatteryCharging, Home, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";
import { BeforeAfterSlider } from "@/components/portfolio/before-after-slider";
import { CaseStudyBreadcrumbs } from "@/components/portfolio/case-study-breadcrumbs";
import { CaseStudyConversion } from "@/components/portfolio/case-study-conversion";
import { CaseStudyCta } from "@/components/portfolio/case-study-cta";
import { CaseStudyEstimateForm } from "@/components/portfolio/case-study-form";
import { CaseStudyStickyCta } from "@/components/portfolio/case-study-sticky-cta";
import { DesignedAroundHome } from "@/components/portfolio/designed-around-home";
import { EnergyFlowSection } from "@/components/portfolio/energy-flow-section";
import { FinalSolarCta } from "@/components/portfolio/final-solar-cta";
import { InstallationCarousel } from "@/components/portfolio/installation-carousel";
import { InstallationGallery } from "@/components/portfolio/installation-gallery";
import {
  CaseStudyTransition,
  PropertyEdgeNav,
  PropertyNav,
  PropertyNavFooter,
  useSwipeNavigation,
  type NavNeighbours,
} from "@/components/portfolio/property-nav";
import { Reveal } from "@/components/portfolio/reveal";
import { SystemConfiguration } from "@/components/portfolio/system-configuration";
import { FiguresExplained, SystemSummaryStrip } from "@/components/portfolio/system-overview";
import { crsCaseStudyEvent } from "@/lib/analytics-events";
import { breadcrumbJsonLdScript, homeBreadcrumb, type BreadcrumbItem } from "@/lib/breadcrumbs";
import {
  caseStudyMetaDescription,
  coverImage,
  caseStudyPageTitle,
  fetchProperties,
  fetchProperty,
  installationH1,
  installationSeoTitle,
  stageImages,
  systemTypeLabel,
  type Property,
} from "@/lib/portfolio";
import { pageSeo } from "@/lib/seo";
import { absoluteUrl, SITE } from "@/lib/site";

const FORM_ID = "solar-estimate-form";
const HERO_ID = "case-study-hero";

function caseStudyBreadcrumbs(property: Property): BreadcrumbItem[] {
  return [
    homeBreadcrumb(),
    { label: "Solar Panels", path: "/solar-pv" },
    { label: "Real Installations", path: "/properties" },
    { label: installationSeoTitle(property), path: `/properties/${property.reference_code}` },
  ];
}

export const Route = createFileRoute("/properties/$referenceCode")({
  loader: async ({ params }) => {
    const all = await fetchProperties();
    const property =
      all.find(
        (item) => item.reference_code.toLowerCase() === params.referenceCode.toLowerCase(),
      ) ?? (await fetchProperty(params.referenceCode));
    if (!property) throw notFound();
    return { property, all };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Installation Unavailable | CRS Energy" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { property } = loaderData;
    const url = absoluteUrl(`/properties/${params.referenceCode}`);
    const seo = pageSeo({
      path: `/properties/${params.referenceCode}`,
      title: caseStudyPageTitle(property),
      description: caseStudyMetaDescription(property),
      ogType: "article",
    });

    const heroImage = coverImage(property.images)?.url ?? null;

    return {
      ...seo,
      links: [
        ...seo.links,
        ...(heroImage
          ? [{ rel: "preload" as const, as: "image" as const, href: heroImage }]
          : []),
      ],
      scripts: [
        breadcrumbJsonLdScript(caseStudyBreadcrumbs(property)),
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            "@id": `${url}#case-study`,
            name: installationH1(property),
            headline: installationH1(property),
            description: caseStudyMetaDescription(property),
            url,
            about: {
              "@type": "Service",
              name: property.has_battery
                ? "Solar panel and battery storage installation"
                : "Solar panel installation",
              provider: {
                "@type": "Organization",
                name: SITE.name,
                "@id": `${SITE.url}/#organization`,
              },
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: InstallationNotFound,
  component: PropertyDetailPage,
});


function InstallationNotFound() {
  return (
    <section className="container-page py-16">
      <h1 className="text-3xl font-bold text-foreground">Installation Not Found</h1>
      <p className="mt-3 text-muted-foreground">
        That link does not match one of our published installations.
      </p>
      <Button asChild className="mt-6">
        <Link to="/properties">Back To All Installations</Link>
      </Button>
    </section>
  );
}

function PropertyDetailPage() {
  const { property, all } = Route.useLoaderData();

  const index = all.findIndex((item) => item.reference_code === property.reference_code);
  const neighbours: NavNeighbours = {
    previous: index > 0 ? all[index - 1] : null,
    next: index >= 0 && index < all.length - 1 ? all[index + 1] : null,
    position: index + 1,
    total: all.length,
  };
  const others = all.filter((item) => item.reference_code !== property.reference_code);

  useSwipeNavigation(neighbours);

  useEffect(() => {
    crsCaseStudyEvent("case_study_viewed", {
      reference_code: property.reference_code,
      once: true,
    });
  }, [property.reference_code]);

  const before = stageImages(property.images, "before")[0];
  const after = stageImages(property.images, "after")[0];
  const showSlider = Boolean(before?.url && after?.url);

  return (
    <>
      <PropertyNav {...neighbours} />
      <PropertyEdgeNav {...neighbours} />

      <CaseStudyTransition referenceCode={property.reference_code}>
        {/* 1. Property hero */}
        <section id={HERO_ID} className="relative overflow-hidden bg-crs-navy text-white">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-crs-blue/25 blur-3xl"
          />
          <div className="container-page relative pb-24 pt-6 md:pb-28 md:pt-10">
            <CaseStudyBreadcrumbs items={caseStudyBreadcrumbs(property)} />

            <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
              <div className="max-w-[46rem]">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-crs-orange">
                  Real CRS Energy Installation
                </p>
                <h1 className="mt-3 text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                  {installationH1(property)}
                </h1>
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white">
                    <Home className="h-4 w-4 text-crs-blue-soft" aria-hidden="true" />
                    {property.house_type}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white">
                    {property.has_battery ? (
                      <BatteryCharging className="h-4 w-4 text-crs-blue-soft" aria-hidden="true" />
                    ) : (
                      <Sun className="h-4 w-4 text-crs-blue-soft" aria-hidden="true" />
                    )}
                    {systemTypeLabel(property)}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white">
                    Installed {property.install_date}
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-3 self-start sm:flex-row sm:items-center lg:flex-col lg:items-stretch lg:self-end">
                <a
                  href={`#${FORM_ID}`}
                  onClick={() =>
                    crsCaseStudyEvent("estimate_cta_clicked", {
                      location: "hero",
                      reference_code: property.reference_code,
                    })
                  }
                  className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-crs-orange px-7 text-base font-semibold text-white shadow-cta transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-crs-navy"
                >
                  Get My Free Solar Estimate
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transform-none"
                    aria-hidden="true"
                  />
                </a>
                <Link
                  to="/properties"
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-white/35 px-7 text-base font-semibold text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-crs-navy"
                >
                  Browse More Installations
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 2 - 5. Photo -> numbers -> configuration -> meaning, one pale-blue module */}
        <div className="bg-crs-blue-pale">
          <div className="container-page">
            <div className="-mt-16 md:-mt-20">
              <InstallationGallery property={property} />
            </div>
            <SystemSummaryStrip property={property} />

            <div className="mt-14 md:mt-20">
              <SystemConfiguration property={property} />
            </div>

            {/* CTA 2 - after the system specification */}
            <div className="mt-12 md:mt-16">
              <CaseStudyCta
                tone="white"
                heading="Wondering What Would Work on Your Roof?"
                copy="Get a personalised solar estimate based on your property and electricity usage."
                location="after_specification"
                referenceCode={property.reference_code}
                formId={FORM_ID}
              />
            </div>

            <div className="mt-14 border-t border-crs-blue-soft pt-14 md:mt-20 md:pt-16">
              <FiguresExplained property={property} />
            </div>

            {showSlider && (
              <div className="mt-14 border-t border-crs-blue-soft pt-14 md:mt-20 md:pt-16">
                <Reveal>
                  <h2 className="text-2xl font-bold text-crs-navy sm:text-3xl">Before and After</h2>
                  <p className="mt-2 max-w-[42rem] text-muted-foreground">
                    Drag to compare the roof before installation with the completed solar panel
                    system.
                  </p>
                  <div className="mt-8">
                    <BeforeAfterSlider property={property} before={before!} after={after!} />
                  </div>
                </Reveal>
              </div>
            )}

            <div className="h-16 md:h-20" />
          </div>
        </div>

        {/* 6. White educational area */}
        <section className="bg-background">
          <div className="container-page py-16 md:py-24">
            <EnergyFlowSection />

            {/* CTA 3 - after the plain-English explanation */}
            <div className="mt-12 md:mt-16">
              <CaseStudyCta
                heading="See What Your Home Could Generate"
                copy="We'll look at your roof, your property and how your household uses electricity."
                ctaLabel="Get My Solar Estimate"
                location="after_explanation"
                referenceCode={property.reference_code}
                formId={FORM_ID}
              />
            </div>
          </div>
        </section>

        {/* 7. Dark conversion with trust integrated */}
        <CaseStudyConversion />

        {/* 8. Browse another installation */}
        {others.length > 0 && (
          <section className="bg-background">
            <div className="container-page py-16 md:py-24">
              <InstallationCarousel properties={others} />

              {/* CTA 4 - after browsing other installations */}
              <div className="mt-12 md:mt-16">
                <CaseStudyCta
                  heading="Found a Home Similar to Yours?"
                  copy="See what could work on your property."
                  location="after_carousel"
                  referenceCode={property.reference_code}
                  formId={FORM_ID}
                />
              </div>
            </div>
          </section>
        )}

        <PropertyNavFooter {...neighbours} />

        {/* 9. Contextual content + embedded estimate form */}
        <section className="bg-crs-blue-pale">
          <div className="container-page py-16 md:py-24">
            <DesignedAroundHome />
            <div className="mx-auto mt-12 max-w-[48rem] md:mt-16">
              <CaseStudyEstimateForm property={property} id={FORM_ID} />
            </div>
          </div>
        </section>

        <FinalSolarCta />
      </CaseStudyTransition>

      <CaseStudyStickyCta
        heroId={HERO_ID}
        formId={FORM_ID}
        referenceCode={property.reference_code}
      />
    </>
  );
}

